// packages/web/src/components/RecordEditor.tsx — R3: the app-wide host for the
// schema-driven record engine. Mounted ONCE in App (it has the live tenantId);
// it renders nothing until a screen calls openRecordEditor(...) or
// openCreateMenu() (see lib/recordEditor.ts). Then it puts up a calm modal:
//
//   • create   → <RecordForm> for the requested type (optionally pre-filled).
//   • edit     → <RecordForm existing> + <RecordActions> (transitions / inline
//                status / delete) for one record — the generic ACT surface on
//                every type, reached from a detail's Edit button.
//   • menu     → the GLOBAL create menu: every creatable type → a create form,
//                so nothing is un-creatable even without a bespoke "+ New".
//
// Any successful mutation calls bumpRecordsVersion() so the read surface behind
// the overlay refreshes. Escape / backdrop / Close all dismiss. Reuses R2's
// RecordForm + RecordActions verbatim — this file is only the shell + wiring.
import { useEffect, useRef, useState } from 'preact/hooks';
import { RecordForm } from './RecordForm';
import { RecordActions } from './RecordActions';
import { PersonAvatarField } from './Avatar';
import {
  ApiError,
  getRecordTypes,
  getRecordTypeSchema,
  getRecordWithLinks,
  type RecordRow,
  type RecordTypeSchema,
  type RecordTypeSummary,
  type LinkRow,
} from '../lib/api';
import {
  bumpRecordsVersion,
  closeCreateMenu,
  closeRecordEditor,
  openRecordEditor,
  useCreateMenuOpen,
  useRecordEditorRequest,
  type EditorRequest,
} from '../lib/recordEditor';
import './RecordEditor.css';

/** Mounted once at the shell level. Renders the create menu and/or the record
 * editor overlay when the store asks for them; otherwise nothing. */
export function RecordEditorHost({ tenantId }: { tenantId: string }) {
  const request = useRecordEditorRequest();
  const menuOpen = useCreateMenuOpen();
  return (
    <>
      {menuOpen && <CreateMenu tenantId={tenantId} onClose={closeCreateMenu} />}
      {request && <EditorOverlay tenantId={tenantId} request={request} onClose={closeRecordEditor} />}
    </>
  );
}

/** A dismissible modal shell — backdrop click + Escape close; the panel traps
 * neither focus loudly nor animates against reduced-motion (see the CSS). */
function Modal({ onClose, labelledBy, children }: { onClose: () => void; labelledBy: string; children: preact.ComponentChildren }) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    // Move focus into the panel so the keyboard lands somewhere sensible.
    panelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div class="re-overlay" onClick={onClose}>
      <div
        class="re-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function EditorOverlay({
  tenantId,
  request,
  onClose,
}: {
  tenantId: string;
  request: EditorRequest;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} labelledBy="re-title">
      <div class="re-head">
        <span class="re-head__eyebrow" id="re-title">
          {request.mode === 'create' ? 'New record' : 'Edit record'}
        </span>
        <button type="button" class="re-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      {request.mode === 'create' ? (
        <CreatePane tenantId={tenantId} request={request} onClose={onClose} />
      ) : (
        <EditPane tenantId={tenantId} typeKey={request.typeKey} id={request.id} onClose={onClose} />
      )}
    </Modal>
  );
}

function CreatePane({
  tenantId,
  request,
  onClose,
}: {
  tenantId: string;
  request: Extract<EditorRequest, { mode: 'create' }>;
  onClose: () => void;
}) {
  return (
    <div class="re-body">
      <RecordForm
        tenantId={tenantId}
        typeKey={request.typeKey}
        prefillFields={request.prefillFields}
        prefillRels={request.prefillRels}
        onSaved={() => {
          bumpRecordsVersion();
          onClose();
        }}
        onCancel={onClose}
      />
    </div>
  );
}

function EditPane({
  tenantId,
  typeKey,
  id,
  onClose,
}: {
  tenantId: string;
  typeKey: string;
  id: string;
  onClose: () => void;
}) {
  const [schema, setSchema] = useState<RecordTypeSchema | null>(null);
  const [existing, setExisting] = useState<{ record: RecordRow; links: LinkRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    Promise.all([getRecordTypeSchema(tenantId, typeKey), getRecordWithLinks(tenantId, id)])
      .then(([s, r]) => {
        if (!alive) return;
        setSchema(s);
        setExisting(r);
      })
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : 'Could not load the record.'));
    return () => {
      alive = false;
    };
  }, [tenantId, typeKey, id, nonce]);

  if (error) {
    return (
      <div class="re-body">
        <div class="re-error" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!schema || !existing) {
    return (
      <div class="re-body">
        <div class="re-loading">Loading…</div>
      </div>
    );
  }

  return (
    <div class="re-body">
      {/* WS5: a person's avatar upload lives at the top of their editor — the
          image slot Wikidata can't fill, shown wherever the person appears. */}
      {typeKey === 'actor_person' && (
        <PersonAvatarField
          tenantId={tenantId}
          personId={existing.record.id}
          name={
            (typeof existing.record.fields['full_name'] === 'string' &&
              (existing.record.fields['full_name'] as string)) ||
            existing.record.display_name
          }
          onUploaded={bumpRecordsVersion}
        />
      )}
      <RecordForm
        tenantId={tenantId}
        typeKey={typeKey}
        schema={schema}
        existing={existing}
        onSaved={() => {
          bumpRecordsVersion();
          onClose();
        }}
        onCancel={onClose}
      />
      <RecordActions
        tenantId={tenantId}
        schema={schema}
        record={existing.record}
        onChanged={() => {
          // An act (transition / status) reloads the record in place so the
          // next legal states are offered, and refreshes the surface behind us.
          bumpRecordsVersion();
          setNonce((n) => n + 1);
        }}
        onDeleted={() => {
          bumpRecordsVersion();
          onClose();
        }}
      />
    </div>
  );
}

function CreateMenu({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const [types, setTypes] = useState<RecordTypeSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getRecordTypes(tenantId)
      .then((t) => alive && setTypes(t))
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : 'Could not load the list.'));
    return () => {
      alive = false;
    };
  }, [tenantId]);

  return (
    <Modal onClose={onClose} labelledBy="re-menu-title">
      <div class="re-head">
        <span class="re-head__eyebrow" id="re-menu-title">
          Create
        </span>
        <button type="button" class="re-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      <div class="re-body">
        {error && (
          <div class="re-error" role="alert">
            {error}
          </div>
        )}
        {!types && !error && <div class="re-loading">Loading…</div>}
        {types && (
          <div class="re-menu" role="menu">
            {types.map((t) => (
              <button
                type="button"
                class="re-menu__item"
                role="menuitem"
                key={t.key}
                onClick={() => openRecordEditor({ mode: 'create', typeKey: t.key })}
                data-testid={`create-type-${t.key}`}
              >
                <span class="re-menu__label">{t.label}</span>
                <span class="re-menu__arrow" aria-hidden="true">
                  ›
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
